/**
 * Holi Colors Effect
 * Slow floating pastel bubbles – fun but not full-screen paint explosion.
 */

import { useEffect, useRef } from 'react'

interface HoliColorsProps {
  intensity?: 'low' | 'medium'
}

const PASTELS = [
  'rgba(255, 182, 193,', // pink
  'rgba(221, 160, 221,', // plum
  'rgba(176, 224, 230,', // powder blue
  'rgba(152, 251, 152,', // mint
  'rgba(255, 218, 185,', // peach
  'rgba(230, 230, 250,', // lavender
]

interface Bubble {
  x: number
  y: number
  r: number
  color: string
  speedY: number
  speedX: number
  alpha: number
}

export function HoliColors({ intensity = 'low' }: HoliColorsProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number>()
  const bubblesRef = useRef<Bubble[]>([])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    const count = intensity === 'low' ? 12 : 20

    const init = () => {
      bubblesRef.current = []
      for (let i = 0; i < count; i++) {
        bubblesRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          r: 8 + Math.random() * 16,
          color: PASTELS[Math.floor(Math.random() * PASTELS.length)],
          speedY: -0.15 - Math.random() * 0.2,
          speedX: (Math.random() - 0.5) * 0.3,
          alpha: 0.04 + Math.random() * 0.05,
        })
      }
    }

    init()

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      bubblesRef.current.forEach((b) => {
        b.y += b.speedY
        b.x += b.speedX
        if (b.y < -b.r * 2) {
          b.y = canvas.height + b.r
          b.x = Math.random() * canvas.width
        }
        if (b.x < -b.r) b.x = canvas.width + b.r
        if (b.x > canvas.width + b.r) b.x = -b.r

        ctx.beginPath()
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2)
        ctx.fillStyle = `${b.color} ${b.alpha})`
        ctx.fill()
      })

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
    }
  }, [intensity])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
      aria-hidden="true"
    />
  )
}
